import { Input, Form, DatePicker } from "antd"
import "./multipleFields.scss"
import { WarningFilled, MailOutlined, PhoneOutlined } from '@ant-design/icons'
import { FlowStepFooter } from "../../flowStep/flowStepFooter.component"
import { useState, useEffect, useRef } from "react"
import FormHelper from "../../helpers/form.helper"
import RuleHelper from "../../../../helpers/rule.helper"
import { useNavigate, useParams } from "react-router-dom"
import dayjs from 'dayjs'
import { Select } from 'antd'
import FieldType from '../../../../enums/fieldType.enum'

const { Item } = Form

export const MultipleFields = ({
  step,
  onSubmit,
  skeleton,
  form,
  isSubmitting,
  hasAttempt,
  flow,
}) => {
  const [rules, setRules] = useState({})
  const { flowType } = useParams()
  const navigate = useNavigate()
  const hasAttemptRef = useRef(null)
  hasAttemptRef.current = hasAttempt

  useEffect(() => {
    fetchRules()
  }, [step])


  useEffect(() => {

    const keyDownHandler = event => {
 
      if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
        event.preventDefault();
        onSubmit()
      }
    };
    
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
      form.resetFields();
    };
  }, []);

  const fetchRules = () => {
    let r = {}  
    
    const allFields = step?.fields?.flatMap(field => 
      field.type === 'group' ? field.fields : [field]
    );

    allFields.forEach(field => {
      let fieldRules = []

        if (field.required) {
          if (field.isSelector) {
            fieldRules.push({ ...RuleHelper.isRequired, message: <><WarningFilled />&nbsp; Please select an option</> })
          } else {
            fieldRules.push(RuleHelper.isRequired)
          }
        }

        if (field.type === FieldType.DATE) {
          fieldRules.push(RuleHelper.isDate)
        }

        if (field.type === FieldType.EMAIL) {
          fieldRules.push(RuleHelper.isEmail) 
        }

        if (field.type === FieldType.EMAIL && field.unique) {

          fieldRules.push(RuleHelper.isUniqueEmail({
            fieldName: field.name,
            navigate, 
            redirectUrl: `/flow/${flowType}/${Object.keys(skeleton)[0]}`,
            userId: flow?.user?._id,
            uniqueError: field.uniqueError
          }))
        }

        if (field.type === FieldType.PHONE) {
          fieldRules.push(RuleHelper.isPhone)
        }

        if (field.type === FieldType.PHONE && field.unique) {
          fieldRules.push(RuleHelper.isUniquePhone({
            fieldName: field.name,
              // navigate, 
              // redirectUrl: `/flow/${flowType}/${Object.keys(skeleton)[0]}`,
            userId: flow?.user?._id,
            uniqueError: field.uniqueError
          }))
        }

        r[field.name] = fieldRules
    })
    
    setRules(r)
  }

  const renderField = (field, fieldIndex, isFirstField) => {
    // Add autoFocus prop if it's the first field
    const autoFocusProps = isFirstField ? { autoFocus: true } : {};

    switch (field.type) {
      case FieldType.DATEPICKER:
        return (
          <DatePicker
            {...autoFocusProps}
            placeholder={field.placeholder}
            className="multiple-input-field"  
            format={(current) => {
              if (current.isSame(dayjs(), 'day')) {
                return 'Today';
              }
              return current.format('MM/DD/YYYY');
            }}  
            disabledDate={(current) => {
              return current && current < dayjs().startOf('day');
            }}
            allowClear={false}
            superNextIcon={null}  // Remove double right arrow (year jump)
            superPrevIcon={null}  // Remove double left arrow (year jump) 
            onChange={(date) => {
              if (hasAttemptRef.current) {
                FormHelper.fetchHasError(form)
              }
            }}
          />
        ) 
      case FieldType.DATE:
        return (
          <Input
            {...autoFocusProps}
            inputMode="numeric"
            placeholder={field.placeholder}
            type='text'
            className="multiple-input-field"
            defaultValue={field?.initialValue}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
              if (value.length > 8) value = value.slice(0, 8);
              
              // Format as MM/DD/YYYY
              if (value.length >= 4) {
                value = value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4);
              } else if (value.length >= 2) {
                value = value.slice(0, 2) + '/' + value.slice(2);
              }
              
              // Update the input value
              e.target.value = value;
              form.setFieldValue(field.name, value);
              
              if (hasAttemptRef.current) {
                FormHelper.fetchHasError(form)
              }
            }}
            maxLength={10}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
          />
        )
      case FieldType.EMAIL:
        return (
          <Input
            {...autoFocusProps}
            placeholder={field.placeholder || "example@email.com"}
            type="email"
            className="multiple-input-field"
            prefix={<MailOutlined style={{ color: '#f5f5f5' }} />}
            onChange={(e) => {
              if (hasAttemptRef.current) {
                FormHelper.fetchHasError(form)
              }
            }}
          />
        )
      case FieldType.PHONE:
        return (
          <Input
            {...autoFocusProps}
            placeholder={field.placeholder || "(800) 555-0175"}
            type="tel"
            inputMode="numeric"
            className="multiple-input-field"  
            prefix={<PhoneOutlined style={{ color: '#f5f5f5' }} />}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
              if (value.length > 10) value = value.slice(0, 10);
              
              // Format as (###) ###-####
              if (value.length >= 6) {
                value = '(' + value.slice(0, 3) + ') ' + value.slice(3, 6) + '-' + value.slice(6);
              } else if (value.length >= 3) {
                value = '(' + value.slice(0, 3) + ') ' + value.slice(3);
              } else if (value.length > 0) {
                value = '(' + value;
              }
              
              // Update the input value
              e.target.value = value;
              form.setFieldValue(field.name, value);
              
              if (hasAttemptRef.current) {
                FormHelper.fetchHasError(form)
              }
            }}
            maxLength={14} // Account for (), space, and hyphen
          />
        )
      case FieldType.SELECT:
        return (
          <Select
            {...autoFocusProps}
            // showSearch={field.showSearch}
            multiple={field.multiple}
            placeholder={field.placeholder || "Select option"}
            onChange={value => {
              if (value) {
                form.setFieldValue(field.name, field.multiple ? [value] : value);
              }
            }}
            options={field.options.map(({ label, value }) => {
              return {
                value: value,
                label: label,
              }
            })}
            className="single-select"
            // filterOption={(input, option) =>
            //   (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            // }
          />
        ) 
      case FieldType.TEXTAREA:
        return (
          <Input.TextArea
            {...autoFocusProps}
            placeholder={field.placeholder}
            className="multiple-input-field"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
            onChange={(e) => {  
              if (hasAttemptRef.current) {
                FormHelper.fetchHasError(form)
              }
            }}
            autoSize={{
              minRows: 2,
              maxRows: 6,
            }}  
          />
        ) 
      case FieldType.TEXT:
      default:
        return (
          <Input
            {...autoFocusProps}
            placeholder={field.placeholder}
            type="default"
            className='multiple-input-field'
            onChange={(e) => {
              if (hasAttemptRef.current) {
                FormHelper.fetchHasError(form)
              }
            }}
            suffix={field.unit}
          />
        )   
     
    }
  }


  return (
    <div className="multiple-fields-container">

      <Form
        form={form}
        className="multiple-input-form"
        layout="vertical"

      >
        {step?.fields.map((field, index) => (
          field.type === FieldType.GROUP ? (
            <div key={`group-${field.label}`} className={`field-group ${field.box ? 'box' : ''}`}>

              <Item label={field.label} required={field.required}>

              {field.fields.map((subField, subIndex) => (
                <div key={`field-${subField.name}`} className={`field-container ${subField.type === FieldType.SELECT ? 'selector-container' : ''}`}>
                  <Item 
                    name={subField.name}
                    placeholder={subField.placeholder}
                    className={subField.type === FieldType.SELECT ? "single-select-item" : "multiple-input-item"}
                    rules={rules[subField.name]}   
                    validateTrigger='onSubmit'
                    initialValue={subField.initialValue}
                  >
                    {renderField(subField, subIndex, index === 0 && subIndex === 0)}
                  </Item>
                </div>
              ))}
              </Item>
            </div>
          ) : (
            <div key={`field-${field.name}`} className={`field-container ${field.type === FieldType.SELECT ? 'selector-container' : ''}`}>
              <Item 
                name={field.name}
                className={field.type === FieldType.SELECT ? "single-select-item" : "multiple-input-item"}
                rules={rules[field.name]}   
                validateTrigger='onSubmit'
                label={field.label}
              >
                {renderField(field, index, index === 0)}
              </Item>
            </div>
          )
        ))} 

        <FlowStepFooter 
          onSubmit={() => onSubmit()} 
          isSubmitting={isSubmitting}
          buttonText={step?.buttonText || "Continue"}
        />
      </Form>
 
    </div>
  )
}             